import { useMemo } from "react";
import { CarReservationDetailSummary } from "@apac/components/cars";
import { MyTripPageHeader } from "@apac/components/ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import type { Itinerary } from "@b2bportal/ground-api";
import type { GroundCancelScenarioV2 } from "@b2bportal/rbc-ground-cancel-v2-api";
import type { CancelModalFlowStepEnum } from "@hopper-b2b/types";
import defaultStyles from "./CarReservationCancellation.module.scss";
import {
  CarCancellationSupport,
  CarTripCancellationPolicy,
} from "@/modules/cars-postbooking/components";
import {
  useCarCancellationView,
  CarCancellationView,
  useCarCancelPolicyBreakdown,
} from "@/modules/cars-postbooking/hooks";
import {
  CancellationError,
  CancellationSuccess,
} from "@/pages/Trips/shared/components";

export interface ICancelGroundModalContentProps {
  itinerary: Itinerary;
  cancelFlowStep: CancelModalFlowStepEnum;
  cancelScenario: GroundCancelScenarioV2;
  onConfirmCancel: () => void;
  /** Re-fetches the cancellation quote. Must never trigger a cancellation. */
  onRetry: () => void;
  /** Leaves the cancellation flow and returns to the trip details. */
  onBack: () => void;
}

const ComponentName = "CarReservationCancellation";

export const CarReservationCancellation = (
  props: ICancelGroundModalContentProps
) => {
  const {
    itinerary,
    cancelFlowStep,
    cancelScenario,
    onConfirmCancel,
    onRetry,
    onBack,
  } = props;

  const { carCancellationView } = useCarCancellationView({
    cancelFlowStep,
  });

  const { breakdown } = useCarCancelPolicyBreakdown(cancelScenario, itinerary);

  const cancelComponent = useMemo(() => {
    switch (carCancellationView) {
      case CarCancellationView.Policy:
        return (
          <CarTripCancellationPolicy
            itinerary={itinerary}
            onCancel={onConfirmCancel}
            cancelScenario={cancelScenario}
          />
        );
      case CarCancellationView.Success: {
        const carBreakdown = breakdown
          ? ({ productType: ProductType.CARS, data: breakdown } as const)
          : null;

        return (
          <CancellationSuccess
            bookingEmail={itinerary.reservation.driverInfo.emailAddress}
            breakdown={carBreakdown}
          />
        );
      }
      case CarCancellationView.Support:
        return (
          <CarCancellationSupport
            cancelScenario={cancelScenario}
            onBack={onBack}
          />
        );
      case CarCancellationView.Error:
        return <CancellationError onRetry={onRetry} />;
    }
  }, [
    cancelScenario,
    carCancellationView,
    itinerary,
    onConfirmCancel,
    onRetry,
    onBack,
    breakdown,
  ]);

  const [block, cn] = useModuleBEM(defaultStyles, ComponentName);

  const displayHeaderAndSideContent =
    carCancellationView === CarCancellationView.Policy;

  return (
    <div className={block}>
      {displayHeaderAndSideContent ? (
        <MyTripPageHeader className={cn("header")} />
      ) : null}

      <div
        className={cn("two-column", {
          singleCol: !displayHeaderAndSideContent,
        })}
      >
        <div className={cn("left-column")}>{cancelComponent}</div>

        {displayHeaderAndSideContent ? (
          <div className={cn("right-column")}>
            {/* TODO(RBC-985): responsive styling for container width */}
            <CarReservationDetailSummary
              className={cn("car-details")}
              reservation={itinerary.reservation}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
